<template>
  <f7-page name="shop-orders">
    <f7-navbar :title="$t('shopPage.myOrders')" back-link />

    <f7-segmented raised round class="margin">
      <f7-button raised round :active="activeTab === 'buyer'" @click="activeTab = 'buyer'">{{ $t('shopPage.purchases') }}</f7-button>
      <f7-button round raised :active="activeTab === 'seller'" @click="activeTab = 'seller'">{{ $t('shopPage.sales') }}</f7-button>
    </f7-segmented>

    <f7-block v-if="loading" class="text-align-center">
      <f7-preloader />
    </f7-block>

    <!-- Buyer Orders -->
    <template v-else-if="activeTab === 'buyer'">
      <div v-if="buyerOrders.length" class="order-list">
        <f7-link
          v-for="order in buyerOrders"
          :key="order.id"
          class="order-card"
          @click="viewOrder(order)"
        >
          <f7-card class="order-card-inner bg-surface-pure shadow-avocash-sm" :padding="false">
            <f7-card-content class="order-card-content" :padding="false">
              <div class="order-header">
                <div class="order-number">#{{ order.orderNumber }}</div>
                <f7-badge :color="statusColor(order.status)">{{ statusLabel(order.status) }}</f7-badge>
              </div>
              <div class="order-body">
                <div class="order-date">{{ formatDate(order.createdAt) }}</div>
                <div class="order-total">{{ formatPrice(order.total) }}</div>
              </div>
              <div class="order-footer">
                <span class="order-items">{{ order.lineCount || '?' }} {{ $t('shopPage.articles') }}</span>
                <f7-icon f7="chevron_right" size="12" color="#8e8e93" />
              </div>
            </f7-card-content>
          </f7-card>
        </f7-link>
      </div>
      <div v-else class="empty-state">
        <f7-icon f7="cart_fill" size="32" color="#8e8e93" />
        <div>{{ $t('shopPage.noOrders') }}</div>
      </div>
    </template>

    <!-- Seller Orders -->
    <template v-else-if="activeTab === 'seller'">
      <div v-if="sellerOrders.length" class="order-list">
        <f7-link
          v-for="order in sellerOrders"
          :key="order.id"
          class="order-card"
          @click="viewOrder(order)"
        >
          <f7-card class="order-card-inner bg-surface-pure shadow-avocash-sm" :padding="false">
            <f7-card-content class="order-card-content" :padding="false">
              <div class="order-header">
                <div class="order-number">#{{ order.orderNumber }}</div>
                <f7-badge :color="statusColor(order.status)">{{ statusLabel(order.status) }}</f7-badge>
              </div>
              <div class="order-body">
                <div class="order-date">{{ formatDate(order.createdAt) }}</div>
                <div class="order-total">{{ formatPrice(order.total) }}</div>
              </div>
              <div class="order-footer">
                <span class="order-items">{{ order.lineCount || '?' }} {{ $t('shopPage.articles') }}</span>
                <f7-icon f7="chevron_right" size="12" color="#8e8e93" />
              </div>
            </f7-card-content>
          </f7-card>
        </f7-link>
      </div>
      <div v-else class="empty-state">
        <f7-icon f7="bag_fill" size="32" color="#8e8e93" />
        <div>{{ $t('shopPage.noSales') }}</div>
      </div>
    </template>
  </f7-page>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { f7 } from 'framework7-vue'
import AppLayout from '@/components/common/AppLayout.vue'
import { useOrderStore } from '@/js/pinia-stores/order.js'
import { useAuthStore } from '@/js/pinia-stores/auth.js'
import i18n from '@/js/i18n.js'

const { t } = i18n.global
defineProps({ f7route: Object, f7router: Object })

const authStore = useAuthStore()
const orderStore = useOrderStore()

const loading = ref(true)
const activeTab = ref('buyer')
const buyerOrders = ref([])
const sellerOrders = ref([])

const STATUS_LABELS = {
  PENDING_PAYMENT: t('shop.pendingPayment'),
  ESCROWED: t('shop.escrowed'),
  ALLOCATED: t('shop.allocated'),
  CONFIRMED: t('shop.confirmed'),
  FULFILLING: t('shop.fulfilling'),
  FULFILLED: t('shop.fulfilled'),
  CANCELLED: t('shop.cancelled'),
  REFUNDED: t('shop.refunded'),
  DISPUTED: t('shop.disputed'),
}

const STATUS_COLORS = {
  PENDING_PAYMENT: 'yellow',
  ESCROWED: 'blue',
  ALLOCATED: 'blue',
  CONFIRMED: 'green',
  FULFILLING: 'orange',
  FULFILLED: 'green',
  CANCELLED: 'gray',
  REFUNDED: 'gray',
  DISPUTED: 'red',
}

onMounted(async () => {
  const userId = authStore.profile?.sub
  if (!userId) {
    loading.value = false
    return
  }
  try {
    const [buyerRes, sellerRes] = await Promise.all([
      orderStore.getByBuyer(userId).catch(() => []),
      orderStore.getByShop(userId).catch(() => []),
    ])
    buyerOrders.value = Array.isArray(buyerRes) ? buyerRes : []
    sellerOrders.value = Array.isArray(sellerRes) ? sellerRes : []
  } catch {
    // keep empty
  } finally {
    loading.value = false
  }
})

function statusLabel(status) { return STATUS_LABELS[status] || status }
function statusColor(status) { return STATUS_COLORS[status] || 'gray' }

function formatDate(dateStr) {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })
}

function formatPrice(amount) {
  return `${Math.round(amount || 0).toLocaleString('fr-FR')} CFA`
}

function viewOrder(order) {
  // Navigate to order detail when page is created
  f7.toast.show({ text: `${t('shopPage.commande')} #${order.orderNumber}`, closeTimeout: 1500 })
}
</script>

<style scoped>
.order-list { padding: 0 12px; }
.order-card {
  color: inherit;
  text-decoration: none;
  margin-bottom: 8px;
}
.order-card-inner {
  margin: 0;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}
.order-card-content {
  padding: 12px;
}
.order-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; }
.order-number { font-size: 13px; font-weight: 600; }
.order-body { display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px; }
.order-date { font-size: 11px; color: var(--f7-on-surface-variant, #8e8e93); }
.order-total { font-size: 15px; font-weight: 600; color: var(--f7-theme-color); }
.order-footer { display: flex; align-items: center; justify-content: space-between; }
.order-items { font-size: 11px; color: var(--f7-on-surface-variant, #8e8e93); }
.empty-state { text-align: center; padding: 40px 16px; color: var(--f7-on-surface-variant, #8e8e93); font-size: 13px; }
</style>
